import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { trackEvent, EVENTS } from "../lib/analytics";
import { useAuth } from "../context/AuthContext";
import { useAccess } from "../context/AccessContext";
import { Button } from "../components/ui/button";
import { Check, Crown, Sparkles } from "lucide-react";
import { toast } from "sonner";

const PREMIUM_ICON = "https://static.prod-images.emergentagent.com/jobs/5446a83f-0e04-4add-9d33-6b8e553aba77/images/16fe641344723d01a8a4c486497afdcc8fd7382cb35b309d60f31b32e38cd772.png";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Premium() {
  const { user, setUser, refresh } = useAuth();
  const { refresh: refreshAccess, setAccess } = useAccess();
  const [plans, setPlans] = useState(null);
  const [billing, setBilling] = useState("yearly");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/premium/plans").then((r) => setPlans(r.data));
    trackEvent(EVENTS.PREMIUM_PAGE_OPENED);
  }, []);

  const features = [
    { icon: "🤖", label: "AI Doubt Solving", desc: "Instant AI-powered help for NEET/JEE questions." },
    { icon: "📊", label: "Mock Test Analytics", desc: "Track every mock test and visualize improvement." },
    { icon: "🧠", label: "AI Performance Insights", desc: "Weak subject detection and personalized recommendations." },
    { icon: "🎯", label: "Score Prediction", desc: "Predict future mock test performance." },
    { icon: "📚", label: "Unlimited Syllabus Tracking", desc: "Unlimited subjects, chapters and goals." },
    { icon: "📈", label: "Daily Progress Reports", desc: "Daily, weekly and monthly analytics." },
    { icon: "📅", label: "Study Calendar", desc: "GitHub-style heatmap of every studied day, streaks and per-day breakdowns." },
    { icon: "🤝", label: "Friends & Study Circle", desc: "Add friends and build your study network." },
    { icon: "💬", label: "Community Feed", desc: "Post doubts, tips, achievements and study updates." },
    { icon: "✉️", label: "Private Messaging", desc: "Chat directly with study partners." },
    { icon: "🧭", label: "Weak Subject Coaching", desc: "AI recommendations based on your weakest subjects." },
    { icon: "🎁", label: "30-Day Premium Trial", desc: "Experience all Premium features free for 30 days." },
  ];

  const whyChoose = [
    { icon: "📈", text: "Improve scores with Mock Test Analytics" },
    { icon: "🧠", text: "Get AI-powered study recommendations" },
    { icon: "🎯", text: "Automatically identify weak subjects" },
    { icon: "💬", text: "Learn with friends and community" },
    { icon: "🚀", text: "Access the complete Syllox experience" },
  ];

  const buy = async () => {
    if (!plans) return;
    setLoading(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error("Couldn't load payments"); return; }
      const { data: order } = await api.post("/premium/order", { plan: billing });

      // Self-heal short-circuit: /premium/order re-checks the user's most
      // recent captured-but-unrecorded payment BEFORE creating a new order.
      // If it found one, the user is ALREADY premium — no Razorpay checkout
      // needed. Refresh contexts and celebrate.
      if (order?.already_paid) {
        await Promise.all([
          refresh().catch(() => {}),
          refreshAccess().catch(() => {}),
        ]);
        toast.success("Your previous payment has been synced. Welcome to Premium! 🎉");
        setLoading(false);
        return;
      }

      const opts = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Syllox Premium",
        description: `${billing === "monthly" ? "Monthly" : "Yearly"} subscription`,
        prefill: {
          name: user?.name,
          email: user?.email || undefined,
          // Razorpay needs `contact` populated (10-digit, no +91) for UPI
          // intent flow to surface GPay / PhonePe / Paytm / BHIM properly.
          contact: user?.phone || undefined,
        },
        // Explicit method enablement — fixes UPI not appearing on some
        // older Razorpay accounts where it's off by default.
        method: { upi: true, card: true, wallet: true, netbanking: true, emi: true },
        // Force INR (UPI works only with INR) and prefer UPI as the first tab.
        config: {
          display: {
            preferences: { show_default_blocks: true },
          },
        },
        notes: { plan: billing },
        theme: { color: "#10B981" },
        modal: { backdropclose: false, escape: false, confirm_close: true, ondismiss: () => setLoading(false) },
        handler: async (resp) => {
          try {
            const { data: verifyResp } = await api.post("/premium/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              plan: billing,
            });
            // ATOMIC state sync — /premium/verify returns both the fresh
            // user AND the fresh access snapshot in one round-trip. We
            // apply them synchronously so every downstream consumer
            // (Layout header badge, Profile page, Settings, Community,
            // Leaderboard, Friends, etc.) sees "Syllox Premium" the
            // moment this handler resolves. No refresh, no logout/login,
            // no race with an in-flight /me/access.
            if (verifyResp?.user) setUser(verifyResp.user);
            if (verifyResp?.access) setAccess(verifyResp.access);
            // Best-effort belt-and-suspenders refresh for edge cases where
            // the server response was truncated or an intermediary cached
            // something. Runs in the background — never blocks the toast.
            refresh().catch(() => {});
            refreshAccess().catch(() => {});
            trackEvent(EVENTS.PREMIUM_PURCHASED, {
              plan: billing,
              amount: order.amount,
              currency: order.currency || "INR",
            });
            toast.success("Welcome to Premium! 🎉");
          } catch {
            toast.error("Payment verification failed");
          }
        },
      };
      const rz = new window.Razorpay(opts);
      rz.on("payment.failed", (resp) => {
        const reason = resp?.error?.description || resp?.error?.reason || "Please try a different payment method";
        toast.error(`Payment failed: ${reason}`);
      });
      rz.open();
    } catch (e) {
      toast.error("Could not start payment");
    } finally {
      setLoading(false);
    }
  };

  const monthly = plans?.plans?.monthly;
  const yearly = plans?.plans?.yearly;

  return (
    <div className="space-y-8" data-testid="premium-root">
      <div className="text-center">
        <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-600">
          <Crown size={14}/> Syllox Premium
        </div>
        <h1 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Unlock the full experience.</h1>
        <p className="mt-2 mx-auto max-w-md text-zinc-600 dark:text-zinc-400">Social features, community, and more — for the price of a chai a month.</p>
      </div>

      <div className="mx-auto flex w-fit rounded-full border border-zinc-200 p-1 dark:border-zinc-800">
        <button onClick={()=>setBilling("monthly")} data-testid="billing-monthly" className={`rounded-full px-4 py-1.5 text-sm font-medium ${billing==="monthly" ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900" : "text-zinc-600 dark:text-zinc-300"}`}>Monthly</button>
        <button onClick={()=>setBilling("yearly")} data-testid="billing-yearly" className={`rounded-full px-4 py-1.5 text-sm font-medium ${billing==="yearly" ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900" : "text-zinc-600 dark:text-zinc-300"}`}>Yearly <span className="ml-1 rounded-full bg-emerald-500 px-1.5 text-[10px] text-white">Best value</span></button>
      </div>

      <div className="mx-auto grid max-w-3xl grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="relative rounded-3xl border border-zinc-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900" data-testid="plan-free">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Free</div>
          <div className="mt-2 font-heading text-3xl font-bold">₹0</div>
          <div className="mt-1 text-sm text-zinc-500">Forever</div>
          <ul className="mt-6 space-y-2 text-sm text-zinc-600 dark:text-zinc-400">
            <li className="flex items-center gap-2"><Check size={16} className="text-emerald-500"/> Syllabus tracker</li>
            <li className="flex items-center gap-2"><Check size={16} className="text-emerald-500"/> Study stopwatch</li>
            <li className="flex items-center gap-2"><Check size={16} className="text-emerald-500"/> Reports & analytics</li>
          </ul>
        </div>

        <div className="relative overflow-hidden rounded-3xl border border-amber-500/40 bg-gradient-to-b from-amber-500/10 to-transparent p-6" data-testid="plan-premium">
          {/* "Most Popular" ribbon — only shown on the Yearly tab per spec */}
          {billing === "yearly" && (
            <div
              className="absolute -right-9 top-5 rotate-45 bg-gradient-to-r from-emerald-500 to-emerald-600 px-10 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-md"
              data-testid="most-popular-badge"
            >
              Most Popular
            </div>
          )}
          <img src={PREMIUM_ICON} alt="" className="absolute -right-6 -top-6 h-32 w-32 opacity-30"/>
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.2em] text-amber-600">Premium</div>

            {/* Pricing block with strikethrough original + discount badge + bold final */}
            {!plans ? (
              <div className="mt-2 font-heading text-3xl font-bold">…</div>
            ) : (
              <div className="mt-3 space-y-1">
                <div className="flex items-center gap-2">
                  <span
                    className="text-base font-medium text-zinc-400 line-through decoration-zinc-400/70"
                    data-testid="price-original"
                  >
                    ₹{(billing === "yearly" ? yearly.original_amount : monthly.original_amount) / 100}
                  </span>
                  <span
                    className="rounded-full bg-emerald-500 px-2 py-0.5 text-[11px] font-bold text-white shadow-sm shadow-emerald-500/30"
                    data-testid="price-discount-badge"
                  >
                    {(billing === "yearly" ? yearly.discount_percent : monthly.discount_percent)}% OFF
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <div
                    className="font-heading text-5xl font-extrabold tracking-tight text-zinc-900 dark:text-white"
                    data-testid="price-final"
                  >
                    ₹{(billing === "yearly" ? yearly.amount : monthly.amount) / 100}
                  </div>
                  <div className="text-sm font-medium text-zinc-500">
                    /{billing === "yearly" ? "year" : "month"}
                  </div>
                </div>
                {billing === "yearly" && monthly && yearly && (
                  <>
                    <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-400" data-testid="price-per-month">
                      Only ₹{Math.round(yearly.amount / 100 / 12)}/month
                    </div>
                    <div className="text-xs text-zinc-500" data-testid="price-savings">
                      Save ₹{(yearly.original_amount - yearly.amount) / 100} every year
                    </div>
                  </>
                )}
              </div>
            )}

            <ul className="mt-6 space-y-2.5 text-sm" data-testid="premium-features-list">
              {features.slice(0, 6).map((f) => (
                <li key={f.label} className="flex items-start gap-2.5">
                  <Check size={16} className="mt-0.5 shrink-0 text-emerald-500"/>
                  <div>
                    <div className="font-medium">{f.label}</div>
                    <div className="text-xs text-zinc-500">{f.desc}</div>
                  </div>
                </li>
              ))}
            </ul>
            {user?.is_premium ? (
              <div className="mt-6 space-y-2">
                <Button disabled className="w-full" data-testid="already-premium-btn"><Crown size={14} className="mr-1"/> You're Premium</Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    if (!window.confirm("Cancel premium? Access continues until your billing period ends. Payments are non-refundable.")) return;
                    try {
                      const { data } = await api.post("/premium/cancel");
                      await refresh();
            await refreshAccess();
                      toast.success(data.message);
                    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                  }}
                  className="w-full text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
                  data-testid="cancel-premium-btn"
                >
                  Cancel premium
                </Button>
                <p className="text-center text-[11px] text-zinc-500">Premium can be cancelled anytime, but payments are non-refundable.</p>
              </div>
            ) : (
              <>
                <Button onClick={buy} disabled={loading} className="mt-6 w-full bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-lg shadow-amber-500/20 hover:from-amber-500 hover:to-orange-600" data-testid="upgrade-btn">
                  <Sparkles size={14} className="mr-1.5"/>{loading ? "Opening…" : "Upgrade now"}
                </Button>
                {/* Refund-policy disclaimer surfaced AT the checkout button.
                    Razorpay disputes lean against merchants when the user
                    claims they weren't shown the policy — this one line
                    materially lowers chargeback risk. */}
                <p
                  className="mt-3 text-center text-[11px] leading-relaxed text-zinc-500 dark:text-zinc-400"
                  data-testid="upgrade-disclaimer"
                >
                  By upgrading, you agree to our{" "}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-emerald-600 underline-offset-2 hover:underline dark:text-emerald-400"
                    data-testid="upgrade-disclaimer-terms-link"
                  >
                    Terms &amp; Conditions
                  </Link>
                  .<br />
                  All Premium purchases are <b>non-refundable</b>.
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Full feature list — shown below the pricing card for clarity */}
      <div className="mx-auto max-w-3xl rounded-3xl border border-zinc-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900" data-testid="premium-full-features">
        <h2 className="font-heading text-xl font-bold tracking-tight">Premium includes</h2>
        <p className="mt-1 text-sm text-zinc-500">Everything you need to study smarter — built for NEET/JEE aspirants.</p>
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {features.map((f) => (
            <div key={f.label} className="flex items-start gap-3 rounded-2xl border border-zinc-200/60 bg-zinc-50/60 p-3 dark:border-zinc-800 dark:bg-zinc-950/40" data-testid={`feature-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}>
              <div className="text-xl shrink-0" aria-hidden="true">{f.icon}</div>
              <div>
                <div className="text-sm font-semibold">{f.label}</div>
                <div className="text-xs text-zinc-500">{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Why students choose premium */}
      <div className="mx-auto max-w-3xl rounded-3xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/5 via-transparent to-transparent p-6" data-testid="why-choose-premium">
        <h2 className="font-heading text-xl font-bold tracking-tight">Why students choose Premium</h2>
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {whyChoose.map((w, i) => (
            <div key={i} className="flex items-center gap-3 rounded-2xl border border-zinc-200/60 bg-white px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900">
              <div className="text-2xl shrink-0" aria-hidden="true">{w.icon}</div>
              <div className="text-sm font-medium">{w.text}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
